import { collection, query, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Contact Service
 * Handles fetching and managing family member contact information
 */

/**
 * Fetch and create a mapping of group IDs/types to group names,
 * plus a member-to-groups lookup for resolving old-style auto-generated IDs
 * @param {string} familyCode - The family code
 * @returns {Promise<Object>} { groupMap, memberGroupsMap }
 */
const getGroupNamesData = async (familyCode) => {
  const groupMap = {};
  const memberGroupsMap = {}; // memberId -> [{ groupId, name }]

  try {
    const familyRef = doc(db, 'families', familyCode);
    const groupsCollection = collection(familyRef, 'groups');
    const groupsSnapshot = await getDocs(groupsCollection);

    groupsSnapshot.docs.forEach(groupDoc => {
      const groupData = groupDoc.data();
      const groupName = groupData.name || groupDoc.id;

      // Map both the document ID and the type to the name
      groupMap[groupDoc.id] = groupName;
      if (groupData.type && groupData.type !== groupDoc.id) {
        groupMap[groupData.type] = groupName;
      }

      // Build reverse lookup: for each member in this group, record the group name
      const members = groupData.members || [];
      members.forEach(memberId => {
        if (!memberGroupsMap[memberId]) {
          memberGroupsMap[memberId] = [];
        }
        memberGroupsMap[memberId].push({
          groupId: groupDoc.id,
          name: groupName
        });
      });
    });
  } catch (error) {
    logger.error('Error fetching group names:', error);
  }

  return { groupMap, memberGroupsMap };
};

/**
 * Resolve group IDs to group objects with names
 * Uses both the groupMap (ID->name) and memberGroupsMap (memberId->groups) for resolution.
 * If a member's stored group IDs are old-style auto-generated Firestore IDs that don't
 * match the current group structure, fall back to the member's actual group memberships.
 * @param {Array} groups - Array of group IDs or group objects
 * @param {Object} groupMap - Map of group ID to group name
 * @param {string} memberId - The member's document ID
 * @param {Object} memberGroupsMap - Map of member ID to their group memberships
 * @returns {Array} Array of group objects with id and name
 */
const resolveGroupNames = (groups, groupMap, memberId, memberGroupsMap) => {
  if (!groups || !Array.isArray(groups)) return [];

  const resolved = groups.map(group => {
    // If it's already an object with a name, return it
    if (typeof group === 'object' && group.name) {
      return group;
    }

    // If it's a string, resolve the name from the map
    const groupId = typeof group === 'string' ? group : group.groupId || group.id;
    const name = groupMap[groupId];

    if (name) {
      return { groupId, name };
    }

    return null; // Could not resolve from groupMap
  });

  // Check if any groups couldn't be resolved (old-style auto-generated IDs)
  const hasUnresolved = resolved.some(g => g === null);

  if (hasUnresolved && memberId && memberGroupsMap[memberId]) {
    // Use the reverse lookup from group documents' members arrays
    return memberGroupsMap[memberId];
  }

  // Filter out nulls and apply fallback formatting for any remaining unresolved
  return resolved.map(g => {
    if (g !== null) return g;
    return null;
  }).filter(g => g !== null);
};

/**
 * Get all contacts for a family
 * @param {string} familyCode - The family code
 * @param {string} currentUserId - Current user's ID (for privacy filtering)
 * @param {boolean} isAdmin - Whether current user is an admin
 * @returns {Promise<Array>} Array of contact objects
 */
export const getFamilyContacts = async (familyCode, currentUserId, isAdmin = false) => {
  try {
    if (!familyCode) {
      throw new Error('Family code is required');
    }

    // Fetch group names map and member-to-groups lookup for resolving group IDs
    const { groupMap, memberGroupsMap } = await getGroupNamesData(familyCode);

    // Query members from NESTED structure: /families/{familyCode}/members
    const familyRef = doc(db, 'families', familyCode);
    const membersRef = collection(familyRef, 'members');
    const membersQuery = query(membersRef, orderBy('displayName'));

    const querySnapshot = await getDocs(membersQuery);
    const contacts = [];

    for (const docSnapshot of querySnapshot.docs) {
      const userData = docSnapshot.data();

      // Resolve group names, using member ID for reverse lookup if needed
      const resolvedGroups = resolveGroupNames(userData.groups || [], groupMap, docSnapshot.id, memberGroupsMap);

      // Build contact object with privacy filtering
      const contact = {
        id: docSnapshot.id,
        displayName: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown User',
        email: userData.email,
        photoURL: userData.photoURL,
        groups: resolvedGroups,
        relationship: userData.relationship,
        occupation: userData.professionalProfile?.profession || userData.professionalProfile?.jobTitle || null,
        birthday: userData.birthday || null,

        // Professional profile
        professionalProfile: userData.professionalProfile || null,

        // Business information
        businessInfo: userData.businessInfo || null,

        // Privacy-aware fields
        phoneNumber: null,
        address: null,

        // Metadata
        isCurrentUser: docSnapshot.id === currentUserId,
        privacySettings: userData.contactPreferences || {
          showEmail: true,
          showPhone: false,
          showAddress: false,
          showGroups: true
        }
      };

      // Apply privacy filtering
      const prefs = contact.privacySettings;

      // Admins can see everything, or user is viewing their own contact
      if (isAdmin || contact.isCurrentUser) {
        contact.phoneNumber = userData.phoneNumber || null;
        contact.address = userData.address || null;
      } else {
        // Apply privacy preferences
        if (prefs.showPhone !== false) {
          contact.phoneNumber = userData.phoneNumber || null;
        }

        if (prefs.showAddress !== false) {
          contact.address = userData.address || null;
        }
      }

      // Email visibility
      if (!isAdmin && !contact.isCurrentUser && prefs.showEmail === false) {
        contact.email = null;
      }

      // Groups visibility
      if (!prefs.showGroups && !isAdmin && !contact.isCurrentUser) {
        contact.groups = [];
      }

      contacts.push(contact);
    }

    return contacts;
  } catch (error) {
    logger.error('Error fetching family contacts:', error);
    throw error;
  }
};

/**
 * Get a single contact by user ID
 * @param {string} userId - User ID to fetch
 * @param {string} currentUserId - Current user's ID
 * @param {boolean} isAdmin - Whether current user is an admin
 * @returns {Promise<Object>} Contact object
 */
export const getContact = async (userId, currentUserId, isAdmin = false) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const prefs = userData.contactPreferences || {
      showEmail: true,
      showPhone: false,
      showAddress: false,
      showGroups: true
    };

    const contact = {
      id: userDoc.id,
      displayName: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown User',
      email: userData.email,
      photoURL: userData.photoURL,
      groups: userData.groups || [],
      relationship: userData.relationship,
      phoneNumber: null,
      address: null,
      isCurrentUser: userId === currentUserId,
      privacySettings: prefs
    };

    // Apply privacy filtering
    if (isAdmin || contact.isCurrentUser) {
      contact.phoneNumber = userData.phoneNumber || null;
      contact.address = userData.address || null;
    } else {
      if (prefs.showPhone !== false) {
        contact.phoneNumber = userData.phoneNumber || null;
      }

      if (prefs.showAddress !== false) {
        contact.address = userData.address || null;
      }

      if (prefs.showEmail === false) {
        contact.email = null;
      }

      if (!prefs.showGroups) {
        contact.groups = [];
      }
    }

    return contact;
  } catch (error) {
    logger.error('Error fetching contact:', error);
    throw error;
  }
};

/**
 * Filter contacts by search term
 * @param {Array} contacts - Array of contacts
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered contacts
 */
export const filterContactsBySearch = (contacts, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return contacts;
  }

  const lowerSearch = searchTerm.toLowerCase();

  return contacts.filter(contact => {
    // Search by name
    if (contact.displayName?.toLowerCase().includes(lowerSearch)) {
      return true;
    }

    // Search by email
    if (contact.email?.toLowerCase().includes(lowerSearch)) {
      return true;
    }

    // Search by phone (remove formatting)
    if (contact.phoneNumber?.replace(/\D/g, '').includes(lowerSearch.replace(/\D/g, ''))) {
      return true;
    }

    // Search by address fields
    if (contact.address) {
      const addressString = `${contact.address.street || ''} ${contact.address.city || ''} ${contact.address.state || ''} ${contact.address.zip || ''}`.toLowerCase();
      if (addressString.includes(lowerSearch)) {
        return true;
      }
    }

    return false;
  });
};

/**
 * Filter contacts by group
 * @param {Array} contacts - Array of contacts
 * @param {string} groupId - Group ID to filter by
 * @returns {Array} Filtered contacts
 */
export const filterContactsByGroup = (contacts, groupId) => {
  if (!groupId || groupId === 'all') {
    return contacts;
  }

  return contacts.filter(contact => {
    // Handle both string IDs and objects
    const groups = contact.groups.map(g => typeof g === 'string' ? g : g.groupId);
    return groups.includes(groupId);
  });
};

/**
 * Export contacts to CSV
 * @param {Array} contacts - Array of contacts
 * @returns {string} CSV string
 */
export const exportToCSV = (contacts) => {
  const headers = ['Name', 'Email', 'Phone', 'Street', 'City', 'State', 'ZIP', 'Country', 'Groups'];
  const rows = contacts.map(contact => [
    contact.displayName || '',
    contact.email || '',
    contact.phoneNumber || '',
    contact.address?.street || '',
    contact.address?.city || '',
    contact.address?.state || '',
    contact.address?.zip || '',
    contact.address?.country || '',
    Array.isArray(contact.groups) ? contact.groups.join('; ') : ''
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(field => `"${field}"`).join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Download CSV file
 * @param {Array} contacts - Array of contacts
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (contacts, filename = 'family-contacts.csv') => {
  const csvContent = exportToCSV(contacts);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
